/**
 * As I'm writing this, I'm realising that our src/hooks/* structure is a
 * getting messy. We have both src/utils src/hooks which share the same purposes
 * and contain uncategorised functions.
 *
 * TODO: Clean up our src/hooks/* and src/utils/* file structure. I'm thinking a
 * domain-first approach.
 */

import { BigNumber } from 'bignumber.js';
import { useCallback, useState } from 'react';

// Like a TypeScript enum but "erasable" so works with `strip-types`
export const AmountInputError = {
  INVALID_FORMAT: 'INVALID_FORMAT',
  TOO_MANY_DECIMALS: 'TOO_MANY_DECIMALS',
  BELOW_MIN: 'BELOW_MIN',
  ABOVE_MAX: 'ABOVE_MAX',
} as const;
export type AmountInputError =
  (typeof AmountInputError)[keyof typeof AmountInputError];

export interface ValidateResult {
  isValid: boolean;
  normalized?: string;
  error?: AmountInputError;
}

interface UseAmountInputProps {
  decimals: number;
  initialValue?: string;
  minValue?: string; // e.g. '0'
  maxValue?: string; // e.g. '1000'
  onChange?: (normalized: string | undefined) => void;
  prefix?: string;
}

function getDecimalSeparator(): string {
  try {
    // Get the decimal separator from the navigator's language
    const formatter = new Intl.NumberFormat(navigator.language, {
      minimumFractionDigits: 1,
    });
    const parts = formatter.formatToParts(1.5);
    const decimalPart = parts.find((part) => part.type === 'decimal');
    return decimalPart?.value || '.';
  } catch {
    // Fallback
    return '.';
  }
}

function toPeriodDecimal(value: string): string {
  const decimalSeparator = getDecimalSeparator();

  // If the decimal separator is a dot return the value as is
  if (decimalSeparator === '.') {
    return value;
  }
  // If the decimal separator is anything else, replace it with a dot
  else {
    return value.replace(new RegExp(decimalSeparator, 'g'), '.');
  }
}

function handleParsedResult(
  result: ValidateResult,
  setError: (error: AmountInputError | undefined) => void,
  onChange?: (normalized: string | undefined) => void
) {
  if (!result.isValid) {
    setError(result.error);
    onChange?.(undefined);
  } else {
    setError(undefined);
    onChange?.(result.normalized);
  }
}

/**
 * Hook for controlled numeric inputs with configurable decimals, min/max bounds,
 * validation, and a user-friendly display. It returns handlers (onChange, onBlur, etc.)
 * and a controlled displayValue, making it simple to integrate with standard <input> elements.
 */
export function useAmountInput({
  decimals,
  initialValue = '',
  minValue = '0',
  maxValue,
  onChange,
  prefix = '',
}: UseAmountInputProps) {
  const [displayValue, setDisplayValue] = useState<string>(initialValue);
  const [error, setError] = useState<AmountInputError | undefined>(undefined);
  const decimalSeparator = getDecimalSeparator();

  // ───────────────────────────────────────────────────────────
  // 1. Parsing & Normalizing
  // ───────────────────────────────────────────────────────────
  const validateValue = useCallback(
    (raw: string): ValidateResult => {
      // Treat empty or just "." as undefined
      if (!raw || raw === '.' || raw === '0.') {
        return { isValid: true };
      }

      const bn = new BigNumber(raw);
      if (bn.isNaN()) {
        return { isValid: false, error: AmountInputError.INVALID_FORMAT };
      }

      // Check decimal places
      const decimalPlaces = bn.dp();
      if (decimalPlaces !== null && decimalPlaces > decimals) {
        return { isValid: false, error: AmountInputError.TOO_MANY_DECIMALS };
      }
      // Check min
      if (bn.isLessThan(minValue)) {
        return { isValid: false, error: AmountInputError.BELOW_MIN };
      }
      // Check max
      if (maxValue && bn.isGreaterThan(maxValue)) {
        return { isValid: false, error: AmountInputError.ABOVE_MAX };
      }
      // All good
      return { isValid: true, normalized: bn.toFixed() };
    },
    [decimals, minValue, maxValue]
  );

  // Formats for display (strips trailing zeros, etc.)
  const formatForDisplay = useCallback(
    (val: string): string => {
      const bn = new BigNumber(val);
      return bn
        .toFixed(decimals)
        .replace(/(\.\d*?)0+$/, '$1')
        .replace(/\.$/, '');
    },
    [decimals]
  );

  // ───────────────────────────────────────────────────────────
  // 2. Event Handlers
  // ───────────────────────────────────────────────────────────
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      let rawInput = e.target.value;

      // Remove any leading prefix (like '$') if present
      if (prefix && rawInput.startsWith(prefix)) {
        rawInput = rawInput.slice(prefix.length);
      }

      // If user starts with the decimal separator, insert a leading zero
      if (rawInput.startsWith(decimalSeparator) || rawInput.startsWith('.')) {
        rawInput = '0.' + rawInput.slice(1);
      }

      const standardized = toPeriodDecimal(rawInput);
      setDisplayValue(standardized);
      handleParsedResult(validateValue(standardized), setError, onChange);
    },
    [onChange, validateValue, prefix, decimalSeparator]
  );

  const handleBlur = useCallback(() => {
    // On blur, reformat the display (unless invalid or empty)
    const { isValid, normalized } = validateValue(displayValue);
    if (isValid && normalized) {
      setDisplayValue(formatForDisplay(normalized));
    }
  }, [displayValue, validateValue, formatForDisplay]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      const { key, metaKey, ctrlKey } = e;

      // 1) Allow standard shortcuts
      //    e.g. CMD + A, CMD + C, CMD + V, etc.
      //    (On Windows, ctrlKey is used; on Mac, metaKey.)
      if (
        (metaKey || ctrlKey) &&
        ['a', 'c', 'v', 'x'].includes(key.toLowerCase())
      ) {
        // Let default behavior happen — e.g. CMD + A = select all
        return;
      }

      // 2) If user hits backspace when the value is exactly '0.', clear the field.
      if (key === 'Backspace' && e.currentTarget.value === '0.') {
        e.preventDefault();
        setDisplayValue('');
        setError(undefined);
        onChange?.(undefined);
        return;
      }

      // 3) Disallow multiple decimal points
      if (
        (key === decimalSeparator || key === '.') &&
        e.currentTarget.value.includes('.')
      ) {
        e.preventDefault();
        return;
      }

      // 4) Restrict to digits, decimal, and recognized control keys
      const isControlKey = key.length > 1; // e.g. Backspace, Arrow keys, etc.
      if (
        !isControlKey &&
        !/[\d]/.test(key) &&
        key !== decimalSeparator &&
        key !== '.'
      ) {
        e.preventDefault();
        return;
      }

      // 5) Prevent typing beyond 'decimals' digits after the decimal
      //    - Only do this check if the user is typing a digit
      //    - Skipped when text is SELECTED: the keystroke REPLACES the
      //      selection, so it cannot overflow the fraction budget — blocking
      //      here made a value that already used every decimal place
      //      impossible to overtype (select-all + type went dead).
      if (!isControlKey && /[0-9]/.test(key)) {
        const {
          selectionStart,
          selectionEnd,
          value: currentValue,
        } = e.currentTarget;
        const hasSelection = selectionStart !== selectionEnd;
        const dotIndex = currentValue.indexOf('.');
        // If there is a dot and the caret (not a selection) is past it
        if (dotIndex !== -1 && !hasSelection) {
          // Number of fractional digits typed so far
          const fractionLength = currentValue.length - (dotIndex + 1);
          if (
            fractionLength >= decimals &&
            selectionStart &&
            selectionStart > dotIndex
          ) {
            // Already have 'decimals' digits after the dot, so prevent more
            e.preventDefault();
          }
        }
      }
    },
    [onChange, setDisplayValue, setError, decimals, decimalSeparator]
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault();
      const pastedText = e.clipboardData.getData('text');

      // NOTE: we do not attempt to standardize the decimal separator here
      // because the localisation issue was primarily for mobile users who
      // had no choice but to use their locale's decimal separator on
      // their mobile keyboard. Additionally, we want to avoid making any
      // unexpected changes to previous experiences.

      // Basic filter of non-digits or non-decimal
      let cleaned = pastedText.replace(/[^\d.]/g, '');

      if (cleaned.startsWith('.')) {
        cleaned = '0.' + cleaned.slice(1);
      }

      setDisplayValue(cleaned);
      handleParsedResult(validateValue(cleaned), setError, onChange);
    },
    [onChange, validateValue]
  );

  const isSemanticallyEqual = useCallback(
    (a: string | undefined, b: string | undefined): boolean => {
      // Both undefined or empty are equal
      if (!a && !b) return true;

      // Special cases for partial inputs
      if (a === '0.' || b === '0.') {
        return !a || !b || a === '0.' || b === '0.';
      }

      // If we have actual values, normalize and compare
      try {
        const aNorm = a ? new BigNumber(a).toFixed(decimals) : undefined;
        const bNorm = b ? new BigNumber(b).toFixed(decimals) : undefined;
        return aNorm === bNorm;
      } catch {
        // If we can't parse either value, they're only equal if exactly the same
        return a === b;
      }
    },
    [decimals]
  );

  // Helper for externally setting the internal display value
  // * We don't trigger onChange here because we're not changing the internal
  // * state and we don't want to trigger a re-render and infinite updates.
  const setValue = useCallback(
    (value: string | undefined) => {
      // If the new value is semantically the same as current, do nothing
      if (isSemanticallyEqual(value, displayValue)) {
        return;
      }

      if (!value) {
        setDisplayValue('');
        setError(undefined);
        return;
      }

      const result = validateValue(value);

      if (result.isValid) {
        const display = formatForDisplay(result.normalized!);
        setDisplayValue(display);
        setError(undefined);
      } else {
        setDisplayValue(value);
        setError(result.error);
      }
    },
    [validateValue, formatForDisplay, displayValue, isSemanticallyEqual]
  );

  return {
    displayValue,
    error,
    handleChange,
    handleBlur,
    handleKeyDown,
    handlePaste,
    setValue,
  };
}
